import type {
  Collider,
  RayColliderHit,
  Vector,
} from "@dimforge/rapier3d-compat";
import { useKeyboardControls } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import {
  quat,
  CapsuleCollider,
  RapierRigidBody,
  RigidBody,
  useRapier,
  type RigidBodyProps,
  vec3,
} from "@react-three/rapier";
import {
  useEffect,
  useRef,
  useMemo,
  type ReactNode,
  forwardRef,
  type RefObject,
} from "react";
import * as THREE from "three";
import SpellIndicator from "./Magic/SpellIndicator";
import { SOL_SPELL_OFFSET } from "./Magic/Spell";
import { useFollowCam } from "../hooks/useFollowCam";
import { useGameStore } from "../stores/gameStore";
import { usePlayerAnimationStateManager } from "../stores/usePlayerAnimationStateManager";
import { GameStates } from "../constants/GameStates";

export { EcctrlAnimation } from "./EcctrlAnimation";

// Retrieve current moving direction of the character
const getMovingDirection = (
  forward: boolean,
  backward: boolean,
  leftward: boolean,
  rightward: boolean,
  pivot: THREE.Object3D
): number | null => {
  if (!forward && !backward && !leftward && !rightward) return null;
  if (forward && leftward) return pivot.rotation.y + Math.PI / 4;
  if (forward && rightward) return pivot.rotation.y - Math.PI / 4;
  if (backward && leftward) return pivot.rotation.y - Math.PI / 4 + Math.PI;
  if (backward && rightward) return pivot.rotation.y + Math.PI / 4 + Math.PI;
  if (backward) return pivot.rotation.y + Math.PI;
  if (leftward) return pivot.rotation.y + Math.PI / 2;
  if (rightward) return pivot.rotation.y - Math.PI / 2;
  if (forward) return pivot.rotation.y;
  return null;
};

const Ecctrl = forwardRef<RapierRigidBody, EcctrlProps>(
  (
    {
      children,
      capsuleHalfHeight = 0.4,
      capsuleRadius = 0.45,
      floatHeight = 0.35,
      characterInitDir = 0, // in rad
      followLight = false,
      // Follow camera setups
      camInitDis = -5,
      camMaxDis = -7,
      camMinDis = -0.7,
      camInitDir = 0, // in rad
      camMoveSpeed = 1,
      camZoomSpeed = 1,
      camCollisionOffset = 0.7,
      // Follow light setups
      followLightPos = { x: 20, y: 30, z: 10 },
      // Base control setups
      maxVelLimit = 2.5,
      turnVelMultiplier = 0.2,
      turnSpeed = 15,
      sprintMult = 2,
      jumpVel = 4,
      jumpForceToGroundMult = 5,
      slopJumpMult = 0.25,
      sprintJumpMult = 1.2,
      airDragMultiplier = 0.2,
      dragDampingC = 0.15,
      accDeltaTime = 8,
      rejectVelMult = 4,
      moveImpulsePointY = 0.5,
      camFollowMult = 11,
      fallingGravityScale = 2.5,
      fallingMaxVel = -20,
      wakeUpDelay = 200,
      // Floating Ray setups
      rayOriginOffest = { x: 0, y: -capsuleHalfHeight, z: 0 },
      rayHitForgiveness = 0.1,
      rayLength = capsuleRadius + 2,
      rayDir = { x: 0, y: -1, z: 0 },
      floatingDis = capsuleRadius + floatHeight,
      springK = 1.2,
      dampingC = 0.08,
      // Slope Ray setups
      showSlopeRayOrigin = false,
      slopeMaxAngle = 1, // in rad
      slopeRayOriginOffest = capsuleRadius - 0.03,
      slopeRayLength = capsuleRadius + 3,
      slopeRayDir = { x: 0, y: -1, z: 0 },
      slopeUpExtraForce = 0.1,
      slopeDownExtraForce = 0.2,
      // AutoBalance Force setups
      autoBalance = true,
      autoBalanceSpringK = 0.3,
      autoBalanceDampingC = 0.03,
      autoBalanceSpringOnY = 0.3,
      autoBalanceDampingOnY = 0.02,
      // Animation temporary setups
      animated = false,
      // Spell casting
      onCastSpell,
      // Other rigibody props from parent
      ...props
    }: EcctrlProps,
    ref
  ) => {
    const characterRef =
      // eslint-disable-next-line react-hooks/rules-of-hooks
      (ref as RefObject<RapierRigidBody>) || useRef<RapierRigidBody>();
    const characterModelRef = useRef<THREE.Group>(null);

    /**
     * Body collider setup
     */
    const modelFacingVec = useMemo(() => new THREE.Vector3(), []);
    const bodyFacingVec = useMemo(() => new THREE.Vector3(), []);
    const bodyBalanceVec = useMemo(() => new THREE.Vector3(), []);
    const bodyBalanceVecOnX = useMemo(() => new THREE.Vector3(), []);
    const bodyFacingVecOnY = useMemo(() => new THREE.Vector3(), []);
    const bodyBalanceVecOnZ = useMemo(() => new THREE.Vector3(), []);
    const vectorY = useMemo(() => new THREE.Vector3(0, 1, 0), []);
    const vectorZ = useMemo(() => new THREE.Vector3(0, 0, 1), []);
    const bodyContactForce = useMemo(() => new THREE.Vector3(), []);

    // Animation change functions
    const idleAnimation = usePlayerAnimationStateManager((state) => state.idle);
    const walkAnimation = usePlayerAnimationStateManager((state) => state.walk);
    const runAnimation = usePlayerAnimationStateManager((state) => state.run);
    const jumpAnimation = usePlayerAnimationStateManager((state) => state.jump);
    const jumpIdleAnimation = usePlayerAnimationStateManager(
      (state) => state.jumpIdle
    );
    const moveAndJumpAnimation = usePlayerAnimationStateManager(
      (state) => state.moveAndJump
    );
    const moveAndJumpIdleAnimation = usePlayerAnimationStateManager(
      (state) => state.moveAndJumpIdle
    );
    const castSpellAnimation = usePlayerAnimationStateManager(
      (state) => state.castSpell
    );
    const fallAnimation = usePlayerAnimationStateManager((state) => state.fall);
    //const additionalAnimation = usePlayerAnimationStateManager((state) => state.triggerFunction);

    /**
     * keyboard controls setup
     */
    const [subscribeKeys, getKeys] = useKeyboardControls();
    const { rapier, world } = useRapier();

    // can jump setup
    let canJump = false;
    let isFalling = false;
    const initialGravityScale: number = useMemo(
      () => props.gravityScale || 1,
      [props.gravityScale]
    );

    // can cast spell setup
    const CAST_RATE = 2000;
    const lastCastSpell = useRef(0);
    let cursorLockNumber = 0;

    // on moving object state
    let massRatio = 1;
    let isOnMovingObject = false;
    const standingForcePoint = useMemo(() => new THREE.Vector3(), []);
    const movingObjectDragForce = useMemo(() => new THREE.Vector3(), []);
    const movingObjectVelocity = useMemo(() => new THREE.Vector3(), []);
    const movingObjectVelocityInCharacterDir = useMemo(
      () => new THREE.Vector3(),
      []
    );
    const distanceFromCharacterToObject = useMemo(
      () => new THREE.Vector3(),
      []
    );
    const objectAngvelToLinvel = useMemo(() => new THREE.Vector3(), []);
    const velocityDiff = useMemo(() => new THREE.Vector3(), []);

    /**
     * Initial light setup
     */
    let dirLight: THREE.DirectionalLight | null = null;

    /**
     * Follow camera initial setups from props
     */
    const cameraSetups = {
      camInitDis,
      camMaxDis,
      camMinDis,
      camMoveSpeed,
      camZoomSpeed,
      camCollisionOffset,
    };

    /**
     * Load camera pivot and character move preset
     */
    const { pivot, cameraCollisionDetect } = useFollowCam(cameraSetups);
    const pivotPosition = useMemo(() => new THREE.Vector3(), []);
    const modelEuler = useMemo(() => new THREE.Euler(), []);
    const modelQuat = useMemo(() => new THREE.Quaternion(), []);
    const moveImpulse = useMemo(() => new THREE.Vector3(), []);
    const movingDirection = useMemo(() => new THREE.Vector3(), []);
    const moveAccNeeded = useMemo(() => new THREE.Vector3(), []);
    const jumpVelocityVec = useMemo(() => new THREE.Vector3(), []);
    const jumpDirection = useMemo(() => new THREE.Vector3(), []);
    const currentVel = useMemo(() => new THREE.Vector3(), []);
    const currentPos = useMemo(() => new THREE.Vector3(), []);
    const dragForce = useMemo(() => new THREE.Vector3(), []);
    const dragAngForce = useMemo(() => new THREE.Vector3(), []);
    const wantToMoveVel = useMemo(() => new THREE.Vector3(), []);
    const rejectVel = useMemo(() => new THREE.Vector3(), []);

    /**
     * Floating Ray setup
     */
    let floatingForce = null;
    const springDirVec = useMemo(() => new THREE.Vector3(), []);
    const characterMassForce = useMemo(() => new THREE.Vector3(), []);
    const rayOrigin = useMemo(() => new THREE.Vector3(), []);
    const rayCast = new rapier.Ray(rayOrigin, rayDir);
    let rayHit: RayColliderHit | null = null;

    /**Test shape ray */
    // const shape = new rapier.Capsule(0.2,0.1)

    /**
     * Slope detection ray setup
     */
    let slopeAngle: number | null = null; // slopeAngle can be a number or null
    let actualSlopeNormal: Vector | undefined;
    let actualSlopeAngle: number | null = null;
    const actualSlopeNormalVec = useMemo(() => new THREE.Vector3(), []);
    const floorNormal = useMemo(() => new THREE.Vector3(0, 1, 0), []);
    const slopeRayOriginRef = useRef<THREE.Mesh>(null);
    const slopeRayorigin = useMemo(() => new THREE.Vector3(), []);
    const slopeRayCast = new rapier.Ray(slopeRayorigin, slopeRayDir);
    let slopeRayHit: RayColliderHit | null = null;

    /**
     * Character moving function
     */
    let characterRotated = true;
    const moveCharacter = (
      _: number,
      run: boolean,
      slopeAngle: number,
      movingObjectVelocity: THREE.Vector3
    ) => {
      /**
       * Setup moving direction
       */
      // Only apply slope extra force when slope angle is between 0.2 and slopeMaxAngle, actualSlopeAngle < slopeMaxAngle
      if (
        actualSlopeAngle! < slopeMaxAngle &&
        Math.abs(slopeAngle) > 0.2 &&
        Math.abs(slopeAngle) < slopeMaxAngle
      ) {
        movingDirection.set(0, Math.sin(slopeAngle), Math.cos(slopeAngle));
      } else if (actualSlopeAngle! >= slopeMaxAngle) {
        movingDirection.set(
          0,
          Math.sin(slopeAngle) > 0 ? 0 : Math.sin(slopeAngle),
          Math.sin(slopeAngle) > 0 ? 0.1 : 1
        );
      } else {
        movingDirection.set(0, 0, 1);
      }

      // Apply character quaternion to moving direction
      movingDirection.applyQuaternion(characterModelRef.current!.quaternion);

      /**
       * Moving object conditions
       */
      // Calculate moving object velocity direction according to character moving direction
      movingObjectVelocityInCharacterDir
        .copy(movingObjectVelocity)
        .projectOnVector(movingDirection)
        .multiply(movingDirection);
      // Calculate angle between moving object velocity direction and character moving direction
      const angleBetweenCharacterDirAndObjectDir =
        movingObjectVelocity.angleTo(movingDirection);

      /**
       * Setup rejection velocity, (currently only work on ground)
       */
      const wantToMoveMeg = currentVel.dot(movingDirection);
      wantToMoveVel.set(
        movingDirection.x * wantToMoveMeg,
        0,
        movingDirection.z * wantToMoveMeg
      );
      rejectVel.copy(currentVel).sub(wantToMoveVel);

      /**
       * Calculate required accelaration and force: a = Δv/Δt
       * If it's on a moving/rotating platform, apply platform velocity to Δv accordingly
       * Also, apply reject velocity when character is moving opposite of it's moving direction
       */
      moveAccNeeded.set(
        (movingDirection.x *
          (maxVelLimit * (run ? sprintMult : 1) +
            movingObjectVelocityInCharacterDir.x) -
          (currentVel.x -
            movingObjectVelocity.x *
              Math.sin(angleBetweenCharacterDirAndObjectDir) +
            rejectVel.x * (isOnMovingObject ? 0 : rejectVelMult))) /
          accDeltaTime,
        0,
        (movingDirection.z *
          (maxVelLimit * (run ? sprintMult : 1) +
            movingObjectVelocityInCharacterDir.z) -
          (currentVel.z -
            movingObjectVelocity.z *
              Math.sin(angleBetweenCharacterDirAndObjectDir) +
            rejectVel.z * (isOnMovingObject ? 0 : rejectVelMult))) /
          accDeltaTime
      );

      // Wanted to move force function: F = ma
      const moveForceNeeded = moveAccNeeded.multiplyScalar(
        characterRef.current!.mass()
      );

      /**
       * Check if character complete turned to the wanted direction
       */
      characterRotated =
        Math.sin(characterModelRef.current!.rotation.y).toFixed(3) ===
        Math.sin(modelEuler.y).toFixed(3);

      // If character hasn't complete turning, change the impulse quaternion follow characterModelRef quaternion
      if (!characterRotated) {
        moveImpulse.set(
          moveForceNeeded.x *
            turnVelMultiplier *
            (canJump ? 1 : airDragMultiplier), // if it's in the air, give it less control
          slopeAngle === null || slopeAngle === 0 // if it's on a slope, apply extra up/down force to the body
            ? 0
            : movingDirection.y *
                turnVelMultiplier *
                (movingDirection.y > 0 // check it is on slope up or slope down
                  ? slopeUpExtraForce
                  : slopeDownExtraForce) *
                (run ? sprintMult : 1),
          moveForceNeeded.z *
            turnVelMultiplier *
            (canJump ? 1 : airDragMultiplier) // if it's in the air, give it less control
        );
      }
      // If character complete turning, change the impulse quaternion default
      else {
        moveImpulse.set(
          moveForceNeeded.x * (canJump ? 1 : airDragMultiplier),
          slopeAngle === null || slopeAngle === 0 // if it's on a slope, apply extra up/down force to the body
            ? 0
            : movingDirection.y *
                (movingDirection.y > 0 // check it is on slope up or slope down
                  ? slopeUpExtraForce
                  : slopeDownExtraForce) *
                (run ? sprintMult : 1),
          moveForceNeeded.z * (canJump ? 1 : airDragMultiplier)
        );
      }

      // Move character at proper direction and impulse
      characterRef.current!.applyImpulseAtPoint(
        moveImpulse,
        {
          x: currentPos.x,
          y: currentPos.y + moveImpulsePointY,
          z: currentPos.z,
        },
        true
      );
    };

    /**
     * Character auto balance function
     */
    const autoBalanceCharacter = () => {
      bodyFacingVec
        .set(0, 0, 1)
        .applyQuaternion(quat(characterRef.current!.rotation()));
      bodyBalanceVec
        .set(0, 1, 0)
        .applyQuaternion(quat(characterRef.current!.rotation()));

      bodyBalanceVecOnX.set(0, bodyBalanceVec.y, bodyBalanceVec.z);
      bodyFacingVecOnY.set(bodyFacingVec.x, 0, bodyFacingVec.z);
      bodyBalanceVecOnZ.set(bodyBalanceVec.x, bodyBalanceVec.y, 0);

      characterModelRef.current!.getWorldDirection(modelFacingVec);
      const crossVecOnX = vectorY.clone().cross(bodyBalanceVecOnX);
      const crossVecOnY = vectorZ.clone().cross(bodyFacingVecOnY);
      const crossVecOnZ = vectorY.clone().cross(bodyBalanceVecOnZ);

      dragAngForce.set(
        (crossVecOnX.x < 0 ? 1 : -1) *
          autoBalanceSpringK *
          bodyBalanceVecOnX.angleTo(vectorY) -
          characterRef.current!.angvel().x * autoBalanceDampingC,
        (crossVecOnY.y < 0 ? 1 : -1) *
          autoBalanceSpringOnY *
          bodyFacingVecOnY.angleTo(vectorZ) -
          characterRef.current!.angvel().y * autoBalanceDampingOnY,
        (crossVecOnZ.z < 0 ? 1 : -1) *
          autoBalanceSpringK *
          bodyBalanceVecOnZ.angleTo(vectorY) -
          characterRef.current!.angvel().z * autoBalanceDampingC
      );
      // Apply balance torque impulse
      characterRef.current!.applyTorqueImpulse(dragAngForce, false);
    };

    /**
     * Character sleep function
     */
    const sleepCharacter = () => {
      if (gameState != GameStates.GAME ) return;
      if (document.visibilityState === "hidden") {
        characterRef.current!.sleep();
      } else {
        setTimeout(() => {
          characterRef.current!.wakeUp();
        }, wakeUpDelay);
      }
    };

    useEffect(() => {
      // Initialize directional light
      if (followLight) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        dirLight = characterModelRef.current!.parent!.parent!.children.find(
          (item) => {
            return item.name === "followLight";
          }
        ) as THREE.DirectionalLight;
      }

      // Trigger key subscribe for special animation
      const unSubscribeCastSpell = subscribeKeys(
        (state) => state.action4,
        (value) => {
          if (value) {
            animated && castSpellAnimation();
          }
        }
      );

      return () => {
        unSubscribeCastSpell();
      };
    });

    useEffect(() => {
      // Lock character rotations at Y axis
      characterRef.current!.setEnabledRotations(
        autoBalance ? true : false,
        autoBalance ? true : false,
        autoBalance ? true : false,
        false
      );
    }, [autoBalance, characterRef]);

    useEffect(() => {
      // Initialize character facing direction
      modelEuler.y = characterInitDir;
      // Initialize camera facing direction
      pivot.rotation.y = camInitDir;

      window.addEventListener("visibilitychange", sleepCharacter);

      return () => {
        window.addEventListener("visibilitychange", sleepCharacter);
      };
    }, [camInitDir, characterInitDir, modelEuler, pivot.rotation]);

    // Getting ready to store character position
    const { playerPosition } = useGameStore((state: any) => ({
      playerPosition: state.playerPosition,
    }));

    const { entityStrength } = useGameStore((state: any) => ({
      entityStrength: state.entityStrength,
    }));

    const { gameState, setGameState } = useGameStore((state: any) => ({
      gameState: state.gameState,
      setGameState: state.setGameState,
    }));

    const { memoryLevel, setMemoryLevel } = useGameStore((state: any) => ({
      memoryLevel: state.memoryLevel,
      setMemoryLevel: state.setMemoryLevel,
    }));

    /**
     * Handles pointer lock change events for spell casting and cursor lock state.
     *
     * Checks if the pointer lock element is a <canvas>, the game is in the GAME state,
     * sufficient time has passed since the last spell cast, and cursorLockNumber is greater than 1.
     * If all conditions are met, increments cursorLockNumber and performs spell casting.
     * Resets cursorLockNumber to 0 if the pointer lock element is null.
     */
    function lockChangeAlert() {
      if (
        document.pointerLockElement?.tagName === "CANVAS" &&
        gameState === GameStates.GAME
      ) {
        cursorLockNumber++;
        if (
          Date.now() - lastCastSpell.current > CAST_RATE &&
          cursorLockNumber > 1
        ) {
          lastCastSpell.current = Date.now();
          const newSpell = {
            id: "sol-" + +new Date(),
            position: vec3(characterRef.current!.translation()),
            angle: modelEuler.y,
            caster: "Sol",
          };
          castSpellAnimation();
          onCastSpell(newSpell);
        }
      } else if (document.pointerLockElement === null) {
        cursorLockNumber = 0;
      }
    }

    /**
     * Handles mouse click events to cast a spell if the Pointer Lock API is not supported,
     * the left mouse button is clicked, the game is in the GAME state, and
     * sufficient time has passed since the last spell cast.
     *
     * @param {MouseEvent} event - The mouse event object.
     */
    const handleMouseClick = (event: MouseEvent) => {
      if (
        !("pointerLockElement" in document) &&
        event.button === 0 &&
        gameState === GameStates.GAME
      ) {
        if (Date.now() - lastCastSpell.current > CAST_RATE) {
          lastCastSpell.current = Date.now();
          const newSpell = {
            id: "sol-" + +new Date(),
            position: vec3(characterRef.current!.translation()),
            angle: modelEuler.y,
            caster: "Sol",
          };
          castSpellAnimation();
          onCastSpell(newSpell);
        }
      }
    };

    useFrame((state, delta) => {
      // Character current position
      if (characterRef.current) {
        currentPos.copy(characterRef.current.translation() as THREE.Vector3);
        // Assign userDate properties
        (characterRef.current.userData as userDataType).canJump = canJump;
        (characterRef.current.userData as userDataType).slopeAngle = slopeAngle;
        (characterRef.current.userData as userDataType).characterRotated =
          characterRotated;
        (characterRef.current.userData as userDataType).isOnMovingObject =
          isOnMovingObject;
      }

      /**
       * Storing character position
       */
      playerPosition.splice(0, playerPosition.length, currentPos);

      /**
       * Apply character position to directional light
       */
      if (followLight && dirLight) {
        dirLight.position.x = currentPos.x + followLightPos.x;
        dirLight.position.y = currentPos.y + followLightPos.y;
        dirLight.position.z = currentPos.z + followLightPos.z;
        dirLight.target = characterModelRef.current!;
      }

      /**
       * Getting all the useful keys from useKeyboardControls
       */
      const { forward, backward, leftward, rightward, jump, run } = getKeys();

      // Getting moving directions (IIFE)
      modelEuler.y = ((movingDirection) =>
        movingDirection === null ? modelEuler.y : movingDirection)(
        getMovingDirection(forward, backward, leftward, rightward, pivot)
      );

      // Move character to the moving direction
      if (forward || backward || leftward || rightward)
        moveCharacter(delta, run, slopeAngle!, movingObjectVelocity);

      // Character current velocity
      if (characterRef.current) {
        currentVel.copy(characterRef.current.linvel() as THREE.Vector3);
      }

      // Jump impulse
      if (jump && canJump) {
        // characterRef.current.applyImpulse(jumpDirection.set(0, 0.5, 0), true);
        jumpVelocityVec.set(
          currentVel.x,
          run ? sprintJumpMult * jumpVel : jumpVel,
          currentVel.z
        );
        // Apply slope normal to jump direction
        characterRef.current!.setLinvel(
          jumpDirection
            .set(
              0,
              (run ? sprintJumpMult * jumpVel : jumpVel) * slopJumpMult,
              0
            )
            .projectOnVector(actualSlopeNormalVec)
            .add(jumpVelocityVec),
          true
        );
        // Apply jump force downward to the standing platform
        characterMassForce.y *= jumpForceToGroundMult;
        rayHit!.collider
          .parent()
          ?.applyImpulseAtPoint(characterMassForce, standingForcePoint, true);
      }

      // Rotate character model
      modelQuat.setFromEuler(modelEuler);
      characterModelRef.current!.quaternion.rotateTowards(
        modelQuat,
        delta * turnSpeed
      );

      /**
       *  Camera movement
       */
      pivotPosition.set(
        currentPos.x,
        currentPos.y + (capsuleHalfHeight + capsuleRadius / 2),
        currentPos.z
      );
      pivot.position.lerp(pivotPosition, 1 - Math.exp(-camFollowMult * delta));
      state.camera.lookAt(pivot.position);

      /**
       * Ray casting detect if on ground
       */
      rayOrigin.addVectors(currentPos, rayOriginOffest as THREE.Vector3);
      rayHit = world.castRay(
        rayCast,
        rayLength,
        true,
        undefined, //null,
        undefined, // null,
        // I have no idea
        characterRef.current as unknown as Collider,
        undefined, //null,
        // this exclude with sensor collider
        (collider) => !collider.isSensor()
      );
      /**Test shape ray */
      // rayHit = world.castShape(
      //   currentPos,
      //   { w: 0, x: 0, y: 0, z: 0 },
      //   {x:0,y:-1,z:0},
      //   shape,
      //   rayLength,
      //   true,
      //   null,
      //   null,
      //   characterRef.current
      // );

      if (rayHit && rayHit.timeOfImpact < floatingDis + rayHitForgiveness) {
        if (slopeRayHit && actualSlopeAngle! < slopeMaxAngle) {
          canJump = true;
        }
      } else {
        canJump = false;
      }

      /**
       * Ray detect if on rigid body or dynamic platform, then apply the linear velocity and angular velocity to character
       */
      if (rayHit && canJump) {
        if (rayHit.collider.parent()) {
          // Getting the standing force apply point
          standingForcePoint.set(
            rayOrigin.x,
            rayOrigin.y - rayHit.timeOfImpact,
            rayOrigin.z
          );
          const rayHitObjectBodyType = rayHit.collider.parent()!.bodyType();
          const rayHitObjectBodyMass = rayHit.collider.parent()!.mass();
          massRatio = characterRef.current!.mass() / rayHitObjectBodyMass;
          // Body type 0 is rigid body, body type 1 is fixed body, body type 2 is kinematic body
          if (rayHitObjectBodyType === 0 || rayHitObjectBodyType === 2) {
            isOnMovingObject = true;

            // Calculate distance between character and moving object
            distanceFromCharacterToObject
              .copy(currentPos)
              .sub(rayHit.collider.parent()!.translation() as THREE.Vector3);
            // Moving object linear velocity
            const movingObjectLinvel = rayHit.collider
              .parent()!
              .linvel() as THREE.Vector3;
            // Moving object angular velocity
            const movingObjectAngvel = rayHit.collider
              .parent()!
              .angvel() as THREE.Vector3;
            // Combine object linear velocity and angular velocity to movingObjectVelocity
            movingObjectVelocity
              .set(
                movingObjectLinvel.x +
                  objectAngvelToLinvel.crossVectors(
                    movingObjectAngvel,
                    distanceFromCharacterToObject
                  ).x,
                movingObjectLinvel.y,
                movingObjectLinvel.z +
                  objectAngvelToLinvel.crossVectors(
                    movingObjectAngvel,
                    distanceFromCharacterToObject
                  ).z
              )
              .multiplyScalar(Math.min(1, 1 / massRatio));
            // If the velocity diff is too high (> 30), ignore movingObjectVelocity
            velocityDiff.subVectors(movingObjectVelocity, currentVel);
            if (velocityDiff.length() > 30)
              movingObjectVelocity.multiplyScalar(1 / velocityDiff.length());

            // Apply opposite drage force to the stading rigid body, body type 0
            // Character moving and unmoving should provide different drag force to the platform
            if (rayHitObjectBodyType === 0) {
              if (!forward && !backward && !leftward && !rightward && canJump) {
                movingObjectDragForce
                  .copy(bodyContactForce)
                  .multiplyScalar(delta)
                  .multiplyScalar(Math.min(1, 1 / massRatio)) // Scale up/down base on different masses ratio
                  .negate();
                bodyContactForce.set(0, 0, 0);
              } else {
                movingObjectDragForce
                  .copy(moveImpulse)
                  .multiplyScalar(Math.min(1, 1 / massRatio)) // Scale up/down base on different masses ratio
                  .negate();
              }
              rayHit.collider
                .parent()!
                .applyImpulseAtPoint(
                  movingObjectDragForce,
                  standingForcePoint,
                  true
                );
            }
          } else {
            // on fixed body
            massRatio = 1;
            isOnMovingObject = false;
            bodyContactForce.set(0, 0, 0);
            movingObjectVelocity.set(0, 0, 0);
          }
        }
      } else {
        // in the air
        massRatio = 1;
        isOnMovingObject = false;
        bodyContactForce.set(0, 0, 0);
        movingObjectVelocity.set(0, 0, 0);
      }

      /**
       * Slope ray casting detect if on slope
       */
      slopeRayOriginRef.current!.getWorldPosition(slopeRayorigin);
      slopeRayorigin.y = rayOrigin.y;
      slopeRayHit = world.castRay(
        slopeRayCast,
        slopeRayLength,
        true,
        undefined, //null,
        undefined, //null,
        // Still no idea
        characterRef.current as unknown as Collider,
        undefined, //null,
        // this exclude with sensor collider
        (collider) => !collider.isSensor()
      );

      // Calculate slope angle
      if (slopeRayHit) {
        actualSlopeNormal = slopeRayHit.collider.castRayAndGetNormal(
          slopeRayCast,
          slopeRayLength,
          false
        )?.normal;
        if (actualSlopeNormal) {
          actualSlopeNormalVec?.set(
            actualSlopeNormal.x,
            actualSlopeNormal.y,
            actualSlopeNormal.z
          );
          actualSlopeAngle = actualSlopeNormalVec?.angleTo(floorNormal);
        }
      }
      if (
        slopeRayHit &&
        rayHit &&
        slopeRayHit.timeOfImpact < floatingDis + 0.5
      ) {
        if (canJump) {
          // Round the slope angle to 2 decimal places
          slopeAngle = Number(
            Math.atan(
              (rayHit.timeOfImpact - slopeRayHit.timeOfImpact) /
                slopeRayOriginOffest
            ).toFixed(2)
          );
        } else {
          slopeAngle = null;
        }
      } else {
        slopeAngle = null;
      }

      /**
       * Apply floating force
       */
      if (rayHit != null) {
        if (canJump && rayHit.collider.parent()) {
          floatingForce =
            springK * (floatingDis - rayHit.timeOfImpact) -
            characterRef.current!.linvel().y * dampingC;
          characterRef.current!.applyImpulse(
            springDirVec.set(0, floatingForce, 0),
            false
          );

          // Apply opposite force to standing object (gravity g in rapier is 0.11 ?_?)
          characterMassForce.set(0, floatingForce > 0 ? -floatingForce : 0, 0);
          rayHit.collider
            .parent()
            ?.applyImpulseAtPoint(characterMassForce, standingForcePoint, true);
        }
      }

      /**
       * Apply drag force if it's not moving
       */
      if (!forward && !backward && !leftward && !rightward && canJump) {
        // not on a moving object
        if (!isOnMovingObject) {
          dragForce.set(
            -currentVel.x * dragDampingC,
            0,
            -currentVel.z * dragDampingC
          );
          characterRef.current!.applyImpulse(dragForce, false);
        }
        // on a moving object
        else {
          dragForce.set(
            (movingObjectVelocity.x - currentVel.x) * dragDampingC,
            0,
            (movingObjectVelocity.z - currentVel.z) * dragDampingC
          );
          characterRef.current!.applyImpulse(dragForce, true);
        }
      }

      /**
       * Detect character falling state
       */
      isFalling = currentVel.y < 0 && !canJump ? true : false;

      /**
       * Apply larger gravity when falling
       */
      if (characterRef.current) {
        if (
          currentVel.y < fallingMaxVel &&
          characterRef.current.gravityScale() !== 0
        ) {
          characterRef.current.setGravityScale(0, true);
        } else if (
          isFalling &&
          currentVel.y > fallingMaxVel &&
          characterRef.current.gravityScale() !== fallingGravityScale
        ) {
          characterRef.current.setGravityScale(fallingGravityScale, true);
        } else if (
          !isFalling &&
          characterRef.current.gravityScale() !== initialGravityScale
        ) {
          characterRef.current.setGravityScale(initialGravityScale, true);
        }
      }

      /**
       * Apply auto balance force to the character
       */
      if (autoBalance && characterRef.current) {
        autoBalanceCharacter();
      }

      /**
       * Camera collision detect
       */
      cameraCollisionDetect(delta);

      /**
       * Apply all the animations
       */
      if (animated) {
        if (
          !forward &&
          !backward &&
          !leftward &&
          !rightward &&
          !jump &&
          canJump
        ) {
          idleAnimation();
        } else if (jump && canJump) {
          forward || backward || leftward || rightward
            ? moveAndJumpAnimation()
            : jumpAnimation();
        } else if (canJump && (forward || backward || leftward || rightward)) {
          run ? runAnimation() : walkAnimation();
        } else if (!canJump) {
          // TODO: check if also moves else regular jump
          forward || backward || leftward || rightward
            ? moveAndJumpIdleAnimation()
            : jumpIdleAnimation();
        }
        // On high sky, play falling animation
        if (rayHit == null && isFalling) {
          fallAnimation();
        }
        // Add event listeners when the component mounts
        document.addEventListener("pointerlockchange", lockChangeAlert, false);
        document.addEventListener("click", handleMouseClick);
      }

      return () => {
        // Clean up event listeners when the component unmounts
        document.removeEventListener(
          "pointerlockchange",
          lockChangeAlert,
          false
        );
        document.removeEventListener("click", handleMouseClick);
      };
    });

    const isHitBySpell = useRef(false);
    const { scrollValue, setScrollValue } = useGameStore((state: any) => ({
      scrollValue: state.scrollValue,
      setScrollValue: state.setScrollValue,
    }));

    const onEntitySpellHit = () => {
      isHitBySpell.current! = true;
      if (memoryLevel <= entityStrength) {
        setGameState(GameStates.GAME_OVER);
      } else if (memoryLevel >= 0) {
        setMemoryLevel(memoryLevel - entityStrength);
        setScrollValue(scrollValue + entityStrength);
      }
      isHitBySpell.current! = false;
    };

    return (
      <RigidBody
        name={"Sol"}
        colliders={false}
        ref={characterRef}
        position={props.position || [0, 5, 0]}
        friction={props.friction || -0.5}
        onContactForce={(e) =>
          bodyContactForce.set(e.totalForce.x, e.totalForce.y, e.totalForce.z)
        }
        onCollisionExit={() => bodyContactForce.set(0, 0, 0)}
        userData={{ canJump: false }}
        {...props}
      >
        <CapsuleCollider
          args={[capsuleHalfHeight, capsuleRadius]}
          onCollisionEnter={({ manifold, target, other }) => {
            if (other.rigidBodyObject!.name === "EntitySpell") {
              onEntitySpellHit();
            }
          }}
        />
        <group ref={characterModelRef} userData={{ camExcludeCollision: true }}>
          {/* This mesh is used for positioning the slope ray origin */}
          <mesh
            position={[
              rayOriginOffest.x,
              rayOriginOffest.y,
              rayOriginOffest.z + slopeRayOriginOffest,
            ]}
            ref={slopeRayOriginRef}
            visible={showSlopeRayOrigin}
            userData={{ camExcludeCollision: true }} // this won't be collide by camera ray
          >
            <boxGeometry args={[0.15, 0.15, 0.15]} />
          </mesh>
          {/* Character model */}
          {children}
          <SpellIndicator
            position={[SOL_SPELL_OFFSET.x, 0, SOL_SPELL_OFFSET.z + 3]}
          />
        </group>
      </RigidBody>
    );
  }
);

export default Ecctrl;

export interface EcctrlProps extends RigidBodyProps {
  children?: ReactNode;
  debug?: boolean;
  capsuleHalfHeight?: number;
  capsuleRadius?: number;
  floatHeight?: number;
  characterInitDir?: number;
  followLight?: boolean;
  // Follow camera setups
  camInitDis?: number;
  camMaxDis?: number;
  camMinDis?: number;
  camInitDir?: number;
  camMoveSpeed?: number;
  camZoomSpeed?: number;
  camCollisionOffset?: number;
  // Follow light setups
  followLightPos?: { x: number; y: number; z: number };
  // Base control setups
  maxVelLimit?: number;
  turnVelMultiplier?: number;
  turnSpeed?: number;
  sprintMult?: number;
  jumpVel?: number;
  jumpForceToGroundMult?: number;
  slopJumpMult?: number;
  sprintJumpMult?: number;
  airDragMultiplier?: number;
  dragDampingC?: number;
  accDeltaTime?: number;
  rejectVelMult?: number;
  moveImpulsePointY?: number;
  camFollowMult?: number;
  fallingGravityScale?: number;
  fallingMaxVel?: number;
  wakeUpDelay?: number;
  // Floating Ray setups
  rayOriginOffest?: { x: number; y: number; z: number };
  rayHitForgiveness?: number;
  rayLength?: number;
  rayDir?: { x: number; y: number; z: number };
  floatingDis?: number;
  springK?: number;
  dampingC?: number;
  // Slope Ray setups
  showSlopeRayOrigin?: boolean;
  slopeMaxAngle?: number;
  slopeRayOriginOffest?: number;
  slopeRayLength?: number;
  slopeRayDir?: { x: number; y: number; z: number };
  slopeUpExtraForce?: number;
  slopeDownExtraForce?: number;
  // AutoBalance Force setups
  autoBalance?: boolean;
  autoBalanceSpringK?: number;
  autoBalanceDampingC?: number;
  autoBalanceSpringOnY?: number;
  autoBalanceDampingOnY?: number;
  // Animation temporary setups
  animated?: boolean;
  // Spell casting
  onCastSpell: (newSpell: any) => void;
  // Other rigibody props from parent
  props?: RigidBodyProps;
}

export interface userDataType {
  canJump?: boolean;
  slopeAngle?: number | null;
  characterRotated?: boolean;
  isOnMovingObject?: boolean;
}
